// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { Doc, Id } from '@/convex/_generated/dataModel';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useConfetti } from '@/hooks/useConfetti';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Link } from 'react-router-dom';
import { SlideSelector } from '@/components/ui/slide-selector';
import { motion, AnimatePresence, HTMLMotionProps } from 'framer-motion';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useState, useMemo } from 'react';

type PodType = {
	_id: Id<'engagementPods'>;
	name: string;
	members: Array<{ email: string }>;
};

type ProfileType = {
	_id: Id<'profiles'>;
	profileTitle: string;
	email: string;
	profileKey: string;
};

type UrlMode = 'broadcast' | 'swarm';

const isPod = (item: any): item is PodType => 'name' in item;
const isProfile = (item: any): item is ProfileType => 'profileTitle' in item;
const truncateUrl = (url: string) => {
	if (url.length > 50) {
		return url.substring(0, 47) + '...';
	}
	return url;
};

const StatusPill = ({ status }: { status: string }) => (
	<span
		className={cn(
			'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center justify-center',
			status === 'linked'
				? '!bg-orange-300 !text-orange-700'
				: '!bg-gray-400 !text-gray-600'
		)}
	>
		{status === 'linked' ? 'LINKED' : 'NOT LINKED'}
	</span>
);

const PlatformPill = ({ platform }: { platform: string }) => (
	<span
		className={cn('px-2 py-1 rounded-full text-xs font-semibold', {
			'bg-purple-300 text-purple-700': platform === 'twitter',
			'bg-blue-300 text-blue-700': platform === 'facebook',
		})}
	>
		{platform.toUpperCase()}
	</span>
);

const PodStatus = ({
	status,
	message,
}: {
	status: string;
	message?: string;
}) => (
	<span
		className={cn('px-2 py-1 rounded-full text-xs font-semibold', {
			'bg-green-300 text-green-700': status === 'success',
			'bg-yellow-300 text-yellow-700': status === 'pending',
			'bg-red-300 text-red-700': status === 'error',
			'bg-gray-300 text-gray-700': status === 'unknown',
		})}
	>
		{status.toUpperCase()}
	</span>
);

export function UrlManagement() {
	const { user, isSuperAdmin } = useAuth();
	const { toast } = useToast();
	const [twitterUrl, setTwitterUrl] = useState('');
	const [facebookUrl, setFacebookUrl] = useState('');
	const [facebookUsername, setFacebookUsername] = useState('');
	const [selectedPodId, setSelectedPodId] = useState<
		Id<'engagementPods'> | Id<'profiles'> | ''
	>('');
	const [activeTab, setActiveTab] = useState('twitter');
	const [urlMode, setUrlMode] = useState<'swarm' | 'broadcast'>('broadcast');
	const [searchTerm, setSearchTerm] = useState('');
	const profiles = useQuery(
		api.profiles.list,
		user?.id
			? {
					userId: user.id as unknown as Id<'users'>,
					isSuperAdmin: Boolean(isSuperAdmin),
				}
			: 'skip'
	);
	const urls = useQuery(
		api.urls.list,
		user?.id
			? {
					podId:
						urlMode === 'swarm'
							? (selectedPodId as Id<'engagementPods'>) || null
							: selectedPodId || null,
					profileKey:
						urlMode === 'broadcast'
							? profiles?.find(
									(p: ProfileType) => p._id === selectedPodId
								)?.profileKey || null
							: null,
					userId: user.id as unknown as Id<'users'>,
					isSuperAdmin: Boolean(isSuperAdmin),
				}
			: 'skip'
	);
	const pods = useQuery(
		api.engagementPods.list,
		user?.id
			? {
					userId: null,
					requestingUserId: user.id as unknown as string,
				}
			: 'skip'
	);

	const addUrl = useMutation(api.urls.addUrl);
	const archiveUrl = useMutation(api.urls.archiveUrl);
	const toggleUrlActive = useMutation(api.urls.toggleUrlActive);
	const assignToPod = useMutation(api.twitterXPosts.assignToPod);

	const { triggerConfetti, Confetti } = useConfetti();

	const [urlToArchive, setUrlToArchive] = useState<Id<'urls'> | null>(null);
	const [urlButtonDisabled, setUrlButtonDisabled] = useState<{
		[key: string]: boolean;
	}>({});

	const [expandedModes, setExpandedModes] = useState<Record<string, boolean>>(
		{
			swarm: false,
			broadcast: false,
		}
	);

	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
		new Set()
	);

	const groupedUrls = useMemo(() => {
		if (!urls) return {};

		return urls.reduce(
			(
				acc: Record<
					string,
					Array<{ mode?: string | undefined; [key: string]: any }>
				>,
				url: { mode?: string | undefined; [key: string]: any }
			) => {
				const mode = url.mode || 'swarm';
				if (!acc[mode]) {
					acc[mode] = [];
				}
				acc[mode].push(url);
				return acc;
			},
			{} as Record<
				string,
				Array<{ mode?: string | undefined; [key: string]: any }>
			>
		);
	}, [urls]);

	const filteredPods = useMemo(() => {
		return (
			pods?.filter(
				(pod: Doc<'engagementPods'>) =>
					pod.platform.toLowerCase() === activeTab.toLowerCase() &&
					pod.isActive
			) || []
		);
	}, [pods, activeTab]);

	const checkUrlExists = useQuery(api.urls.checkUrlExists, {
		url: twitterUrl || facebookUrl,
		podId:
			urlMode === 'swarm'
				? (selectedPodId as Id<'engagementPods'>) || null
				: null,
		profileKey:
			urlMode === 'broadcast'
				? profiles?.find((p: ProfileType) => p._id === selectedPodId)
						?.profileKey || null
				: null,
		mode: urlMode,
	});

	const renderModeSelect = () => (
		<div className='mb-6'>
			<label className='block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300'>
				Engagement Mode
			</label>
			<SlideSelector
				value={urlMode}
				onChange={(value) => {
					setUrlMode(value as 'swarm' | 'broadcast');
					setSelectedPodId('');
				}}
				options={[
					{
						value: 'broadcast',
						label: 'Broadcast Mode',
						icon: '📢',
						description: 'One Member Per Pod',
						className:
							'bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700',
					},
					{
						value: 'swarm',
						label: 'Swarm Mode',
						icon: '🐝',
						description: 'All Pod Members Engage',
						className:
							'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
					},
				]}
				className='w-full'
				activeColor={urlMode === 'broadcast' ? 'orange' : 'purple'}
			/>
		</div>
	);

	const handleTwitterSubmit = async () => {
		try {
			if (!selectedPodId) {
				toast({
					title: 'Required',
					description:
						urlMode === 'swarm'
							? 'Please select an engagement pod'
							: 'Please select a profile',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			let username: string;
			let conversationId: string | null = null;

			if (urlMode === 'swarm') {
				// Swarm mode requires post URLs
				const postRegex = /x\.com\/([^/]+)\/status\/(\d+)/;
				const match = twitterUrl.match(postRegex);
				if (!match) {
					toast({
						title: 'Invalid URL Format',
						description:
							'Please enter a valid Twitter post URL (e.g., https://x.com/username/status/123)',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}
				[, username, conversationId] = match;
			} else {
				// Broadcast mode requires profile URLs
				const profileRegexX = /x\.com\/([^/]+)/;
				const profileRegexTwitter = /twitter\.com\/([^/]+)/;
				const profileMatchX = twitterUrl.match(profileRegexX);
				const profileMatchTwitter =
					twitterUrl.match(profileRegexTwitter);
				const profileMatch = profileMatchX || profileMatchTwitter;

				if (!profileMatch) {
					toast({
						title: 'Invalid URL Format',
						description:
							'Please enter a valid Twitter profile URL (e.g., https://x.com/username or https://twitter.com/username)',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}

				// For profile URLs, extract username but skip status check
				const usernameMatch = profileMatch[1].split('/')[0]; // Get username before any additional path segments
				username = usernameMatch;
			}

			// Normalize username to lowercase
			username = username.toLowerCase();

			// Check for duplicate URL with the correct pod/profile ID
			if (checkUrlExists) {
				toast({
					title: 'Duplicate URL',
					description:
						urlMode === 'swarm'
							? 'This URL has already been added to this pod'
							: 'This URL has already been added to this profile',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			// Add URL with the correct pod/profile assignment
			const urlResult = await addUrl({
				platform: activeTab,
				url: twitterUrl,
				username,
				conversationId: conversationId || undefined,
				userId: user?.id as unknown as Id<'users'>,
				podId:
					urlMode === 'swarm'
						? (selectedPodId as Id<'engagementPods'>)
						: null,
				profileKey:
					urlMode === 'broadcast'
						? profiles?.find(
								(p: ProfileType) => p._id === selectedPodId
							)?.profileKey || null
						: null,
				isActive: true,
				mode: urlMode,
			});

			// Only assign to pod if it's swarm mode and we have a postId
			if (urlMode === 'swarm' && urlResult.postId) {
				await assignToPod({
					postId: urlResult.postId as Id<'twitterXPosts'>,
					podId: selectedPodId as Id<'engagementPods'>,
				});
			}

			setTwitterUrl('');
			setSelectedPodId('');
			triggerConfetti();
			toast({
				title: urlResult.isExisting
					? '✨ URL Updated ✨'
					: '✨ URL Added ✨',
				description: `Successfully ${urlResult.isExisting ? 'updated' : 'added'} URL for @${username}`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to process URL',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleFacebookSubmit = async () => {
		try {
			if (!selectedPodId) {
				toast({
					title: 'Pod Required',
					description: 'Please select an engagement pod',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			const fbRegex = /fbid=(\d+)/;
			const match = facebookUrl.match(fbRegex);
			const socialUsername = facebookUsername.trim();

			if (!match) {
				toast({
					title: 'Invalid URL Format',
					description:
						'Please enter a valid Facebook URL (e.g., https://www.facebook.com/photo?fbid=123)',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			if (!socialUsername) {
				toast({
					title: 'Username Required',
					description: 'Please enter your Facebook username',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			// Check for duplicate URL
			if (checkUrlExists) {
				toast({
					title: 'Duplicate URL',
					description: 'This URL has already been added to this pod',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			const [, conversationId] = match;

			await addUrl({
				platform: activeTab,
				url: facebookUrl,
				conversationId,
				userId: user?.id as unknown as Id<'users'>,
				username: socialUsername,
				podId:
					urlMode === 'swarm'
						? (selectedPodId as Id<'engagementPods'>)
						: null,
				profileKey:
					urlMode === 'broadcast'
						? profiles?.find(
								(p: ProfileType) => p._id === selectedPodId
							)?.profileKey || null
						: null,
				isActive: true,
				mode: urlMode,
			});

			setFacebookUrl('');
			setFacebookUsername('');
			setSelectedPodId('');
			triggerConfetti();

			toast({
				title: '✨ Facebook URL Added ✨',
				description: `Successfully processed URL with ID: ${conversationId}`,
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to process URL',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleArchiveUrl = async (urlId: Id<'urls'>) => {
		try {
			await archiveUrl({ urlId });
			toast({
				title: '📦 URL Archived',
				description:
					'URL has been archived successfully. View it in the history page.',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to archive URL',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const handleToggleUrlActive = async (urlId: Id<'urls'>) => {
		try {
			// Disable the button
			setUrlButtonDisabled((prev) => ({ ...prev, [urlId]: true }));

			const url = urls?.find((u: Doc<'urls'>) => u._id === urlId);
			if (!url) throw new Error('URL not found');

			await toggleUrlActive({ urlId });

			toast({
				title: '✨ URL Updated ✨',
				description: 'URL status updated successfully.',
				duration: 3000,
			});

			// Enable the button after 5 seconds
			setTimeout(() => {
				setUrlButtonDisabled((prev) => ({ ...prev, [urlId]: false }));
			}, 5000);
		} catch (error) {
			// Enable the button immediately if there's an error
			setUrlButtonDisabled((prev) => ({ ...prev, [urlId]: false }));
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to update URL status',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const renderUrlsTable = (platform: string) => {
		if (!urls) return null;

		const platformUrls = urls.filter(
			(url: Doc<'urls'>) => url.platform === platform && !url.isArchived
		);
		if (!platformUrls.length) {
			return (
				<Card className='mt-4'>
					<CardContent className='pt-6'>
						<p className='text-center text-gray-500'>
							No URLs added yet
						</p>
					</CardContent>
				</Card>
			);
		}

		// Group URLs
		const groupedByMode: Record<UrlMode, Record<string, typeof urls>> = {
			broadcast: {},
			swarm: {},
		};

		// Group URLs by mode and ID
		platformUrls.forEach((url: Doc<'urls'>) => {
			const mode = (url.mode || 'swarm') as UrlMode;
			if (mode === 'swarm') {
				const groupId = url.podId;
				if (groupId) {
					if (!groupedByMode[mode][groupId])
						groupedByMode[mode][groupId] = [];
					groupedByMode[mode][groupId].push(url);
				}
			} else {
				// For broadcast mode, find the profile ID that matches the profileKey
				const profile = profiles?.find(
					(p: ProfileType) => p.profileKey === url.profileKey
				);
				const groupId = profile?._id;
				if (groupId) {
					if (!groupedByMode[mode][groupId])
						groupedByMode[mode][groupId] = [];
					groupedByMode[mode][groupId].push(url);
				}
			}
		});

		// Sort URLs in each group by date
		Object.values(groupedByMode).forEach((groups) => {
			Object.values(groups).forEach((urls) => {
				urls.sort(
					(a: Doc<'urls'>, b: Doc<'urls'>) =>
						new Date(b.createdAt).getTime() -
						new Date(a.createdAt).getTime()
				);
			});
		});

		// Sort groups by their most recent URL
		const sortGroups = (groups: Record<string, typeof urls>) =>
			Object.entries(groups).sort(
				([, a], [, b]) =>
					new Date(b[0].createdAt).getTime() -
					new Date(a[0].createdAt).getTime()
			);

		return (
			<div className='mt-8 space-y-8'>
				{(['broadcast', 'swarm'] as UrlMode[]).map((mode) => {
					const groups = groupedByMode[mode];
					if (Object.keys(groups).length === 0) return null;

					const sortedGroups = sortGroups(groups);

					return (
						<Card key={mode}>
							<CardContent className='pt-6'>
								<div className='space-y-4'>
									<div className='flex items-center gap-2'>
										<div
											className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors cursor-pointer'
											onClick={() =>
												setExpandedModes((prev) => ({
													...prev,
													[mode]: !prev[mode],
												}))
											}
										>
											<motion.div
												animate={{
													rotate: expandedModes[mode]
														? 180
														: 0,
												}}
												transition={{
													duration: 0.2,
													ease: 'easeInOut',
												}}
											>
												🔽
											</motion.div>
										</div>
										<h2 className='text-3xl font-bold text-pink-500 uppercase'>
											{mode} Mode
										</h2>
									</div>
									<p className='text-lg text-gray-500 mb-4'>
										Active URLs:{' '}
										{Object.values(groups).flat().length}
									</p>
									<AnimatePresence>
										{expandedModes[mode] && (
											<motion.div
												className='space-y-8'
												initial={{
													opacity: 0,
													height: 0,
												}}
												animate={{
													opacity: 1,
													height: 'auto',
												}}
												exit={{ opacity: 0, height: 0 }}
												transition={{ duration: 0.2 }}
											>
												{sortedGroups.map(
													([groupId, groupUrls]) => {
														if (!groupUrls)
															return null;

														const groupName =
															mode === 'swarm'
																? pods?.find(
																		(
																			p: PodType
																		) =>
																			p._id ===
																			groupId
																	)?.name
																: profiles?.find(
																		(
																			p: ProfileType
																		) =>
																			p._id ===
																			groupId
																	)
																		?.profileTitle;

														return (
															<div
																key={groupId}
																className='border border-gray-200 dark:border-gray-800 rounded-lg p-4'
															>
																<div className='space-y-2'>
																	<div className='flex items-center gap-2'>
																		<div
																			className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors cursor-pointer'
																			onClick={() =>
																				setExpandedGroups(
																					(
																						prev
																					) => {
																						const next =
																							new Set(
																								prev
																							);
																						if (
																							next.has(
																								groupId
																							)
																						) {
																							next.delete(
																								groupId
																							);
																						} else {
																							next.add(
																								groupId
																							);
																						}
																						return next;
																					}
																				)
																			}
																		>
																			{expandedGroups.has(
																				groupId
																			)
																				? '🔽'
																				: '▶️'}
																		</div>
																		<h3 className='text-xl font-semibold text-purple-500'>
																			{groupName ||
																				'Unknown Group'}
																		</h3>
																	</div>
																	{mode ===
																		'broadcast' &&
																		profiles?.find(
																			(
																				p: ProfileType
																			) =>
																				p._id ===
																				groupId
																		) && (
																			<>
																				<div className='flex items-center gap-2'>
																					<StatusPill
																						status={
																							profiles
																								?.find(
																									(
																										p: ProfileType
																									) =>
																										p._id ===
																										groupId
																								)
																								?.linkStatuses?.find(
																									(status: {
																										platform: string;
																										status: string;
																									}) =>
																										status.platform.toLowerCase() ===
																										platform.toLowerCase()
																								)
																								?.status ||
																							'not_linked'
																						}
																					/>
																					<PlatformPill
																						platform={
																							platform
																						}
																					/>
																				</div>
																				<p className='text-sm text-gray-500'>
																					{
																						profiles.find(
																							(
																								p: ProfileType
																							) =>
																								p._id ===
																								groupId
																						)
																							?.email
																					}
																				</p>
																			</>
																		)}
																	{mode ===
																		'swarm' &&
																		pods?.find(
																			(
																				p: PodType
																			) =>
																				p._id ===
																				groupId
																		) && (
																			<div className='flex items-center gap-2'>
																				<PodStatus
																					status={
																						pods.find(
																							(
																								p: PodType
																							) =>
																								p._id ===
																								groupId
																						)
																							?.lastStatus
																							?.status ||
																						'unknown'
																					}
																					message={
																						pods.find(
																							(
																								p: PodType
																							) =>
																								p._id ===
																								groupId
																						)
																							?.lastStatus
																							?.message
																					}
																				/>
																				<span className='text-sm text-gray-500'>
																					(
																					{pods.find(
																						(
																							p: PodType
																						) =>
																							p._id ===
																							groupId
																					)
																						?.members
																						?.length ||
																						0}{' '}
																					members)
																				</span>
																			</div>
																		)}
																	<p className='text-sm text-gray-500'>
																		URLs in
																		group:{' '}
																		{
																			groupUrls.length
																		}
																	</p>
																</div>
																<AnimatePresence>
																	{expandedGroups.has(
																		groupId
																	) && (
																		<motion.div
																			className='space-y-4 mt-4'
																			initial={{
																				opacity: 0,
																				height: 0,
																			}}
																			animate={{
																				opacity: 1,
																				height: 'auto',
																			}}
																			exit={{
																				opacity: 0,
																				height: 0,
																			}}
																			transition={{
																				duration: 0.2,
																			}}
																		>
																			{groupUrls.map(
																				(
																					url: Doc<'urls'>,
																					idx: number
																				) => (
																					<motion.div
																						key={
																							url._id
																						}
																						initial={{
																							opacity: 0,
																							y: -10,
																						}}
																						animate={{
																							opacity: 1,
																							y: 0,
																							transition:
																								{
																									duration: 0.2,
																									delay:
																										idx *
																										0.05,
																								},
																						}}
																					>
																						<Card className='p-4'>
																							<div>
																								<div className='flex justify-between items-start'>
																									<div className='space-y-2'>
																										<div className='flex items-center gap-2'>
																											<h3 className='font-semibold'>
																												{url.username ||
																													'Unknown User'}
																											</h3>
																											<span
																												className={cn(
																													'px-4 py-2 rounded-full text-sm font-bold',
																													url.isActive
																														? 'bg-emerald-200 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
																														: 'bg-red-200 dark:bg-red-900 text-red-800 dark:text-red-200'
																												)}
																											>
																												{url.isActive
																													? 'Active'
																													: 'Inactive'}
																											</span>
																											<span className='px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800'>
																												{platform.toUpperCase()}
																											</span>
																										</div>
																										<div className='flex items-center gap-2'>
																											<p className='text-sm text-gray-500 font-mono'>
																												<a
																													href={
																														url.url
																													}
																													target='_blank'
																													rel='noopener noreferrer'
																													className='text-blue-500 hover:text-blue-700 underline'
																												>
																													{truncateUrl(
																														url.url
																													)}
																												</a>
																											</p>
																										</div>
																										<p className='text-xs text-gray-400'>
																											Added:{' '}
																											{new Date(
																												url.createdAt
																											).toLocaleString()}
																										</p>
																									</div>
																									<div className='flex gap-2'>
																										<Button
																											variant='outline'
																											size='sm'
																											onClick={() =>
																												handleToggleUrlActive(
																													url._id
																												)
																											}
																											disabled={
																												urlButtonDisabled[
																													url
																														._id
																												]
																											}
																											className={
																												url.isActive
																													? 'bg-orange-500 hover:bg-orange-600 text-white'
																													: 'bg-emerald-600 hover:bg-emerald-700 text-white'
																											}
																										>
																											{url.isActive
																												? '🚫 Deactivate'
																												: '✅ Activate'}
																										</Button>
																										<Button
																											variant='outline'
																											size='sm'
																											onClick={() =>
																												setUrlToArchive(
																													url._id
																												)
																											}
																											className='bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 whitespace-nowrap'
																										>
																											📦
																											Archive
																										</Button>
																									</div>
																								</div>
																							</div>
																						</Card>
																					</motion.div>
																				)
																			)}
																		</motion.div>
																	)}
																</AnimatePresence>
															</div>
														);
													}
												)}
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		);
	};

	const renderPodSelect = () => {
		const items = urlMode === 'swarm' ? filteredPods : profiles;
		const filteredItems =
			items?.filter((item: PodType | ProfileType) => {
				const searchString = searchTerm.toLowerCase();
				if (urlMode === 'swarm') {
					return (
						'name' in item &&
						item.name.toLowerCase().includes(searchString)
					);
				} else {
					return (
						'profileTitle' in item &&
						(item.profileTitle
							.toLowerCase()
							.includes(searchString) ||
							item.email.toLowerCase().includes(searchString))
					);
				}
			}) || [];

		return (
			<div className='mb-6'>
				<label className='block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300'>
					{urlMode === 'swarm' ? 'Engagement Pod' : 'Profile'}
				</label>
				<SearchableSelect
					value={selectedPodId}
					onValueChange={(value) =>
						setSelectedPodId(
							value as Id<'engagementPods'> | Id<'profiles'>
						)
					}
					items={[
						...(urlMode === 'swarm' ? pods || [] : []),
						...(urlMode === 'broadcast' ? profiles || [] : []),
					]}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					placeholder={`Select ${urlMode === 'swarm' ? 'Pod' : 'Profile'}`}
					valueField='_id'
					renderItem={(item) => (
						<span>
							{urlMode === 'swarm'
								? item.name
								: `${item.profileTitle} (${item.email})`}
						</span>
					)}
				/>
			</div>
		);
	};

	return (
		<div className='container mx-auto p-6 pb-80'>
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-3xl font-bold py-8'>Manage Links</h1>
				<div className='flex gap-2'>
					<Button variant='outline' asChild>
						<Link to='/admin/urls/history'>
							📦 View Archived URLs
						</Link>
					</Button>
				</div>
			</div>
			<Confetti />

			<Tabs
				defaultValue='twitter'
				className='space-y-4'
				onValueChange={setActiveTab}
			>
				<TabsList>
					<TabsTrigger value='twitter'>Twitter X</TabsTrigger>
					<TabsTrigger value='facebook'>Facebook</TabsTrigger>
				</TabsList>

				<TabsContent value='twitter'>
					<Card className='glow-card'>
						<CardHeader>
							<CardTitle className='pink-title'>
								Add Twitter URL
							</CardTitle>
						</CardHeader>
						<CardContent>
							{renderModeSelect()}
							{renderPodSelect()}
							<div className='flex gap-4'>
								<Input
									placeholder='Enter Twitter URL (e.g., https://x.com/username or https://x.com/username/status/123)'
									value={twitterUrl}
									onChange={(e) =>
										setTwitterUrl(e.target.value)
									}
									className='flex-1'
								/>
								<Button onClick={handleTwitterSubmit}>
									Add URL
								</Button>
							</div>
						</CardContent>
					</Card>
					{renderUrlsTable('twitter')}
				</TabsContent>

				<TabsContent value='facebook'>
					<Card className='glow-card'>
						<CardHeader>
							<CardTitle className='pink-title'>
								Add Facebook URL
							</CardTitle>
						</CardHeader>
						<CardContent>
							{renderModeSelect()}
							{renderPodSelect()}
							<div className='flex gap-4'>
								<Input
									placeholder='Enter Facebook URL (e.g., https://www.facebook.com/photo?fbid=123)'
									value={facebookUrl}
									onChange={(e) =>
										setFacebookUrl(e.target.value)
									}
									className='flex-1'
								/>
							</div>
							<div className='flex gap-4 mb-4 w-80 my-4'>
								<Input
									placeholder='Enter the Facebook username for the URL'
									value={facebookUsername}
									onChange={(e) =>
										setFacebookUsername(e.target.value)
									}
									className='flex-1'
								/>
							</div>
							<Button onClick={handleFacebookSubmit}>
								Add URL
							</Button>
						</CardContent>
					</Card>
					{renderUrlsTable('facebook')}
				</TabsContent>
			</Tabs>

			<ConfirmDialog
				isOpen={urlToArchive !== null}
				onClose={() => setUrlToArchive(null)}
				onConfirm={() => {
					if (urlToArchive) {
						handleArchiveUrl(urlToArchive);
						setUrlToArchive(null);
					}
				}}
				title='Archive URL'
				description='Are you sure you want to archive this URL? You can view archived URLs in the history page.'
				confirmText='Archive'
				cancelText='Cancel'
			/>
		</div>
	);
}
